import React, { useState } from 'react';
import { View, Text, Pressable, TextInput } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import { usePathname } from 'expo-router';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import Input from '@/components/General/inputs/Input';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Unicon from '@/components/General/Unicon';
import Toast from 'react-native-toast-message';
import AddPersonQuickForm from '@/components/People/AddPersonQuickForm';
import PrintableCheckInModal from '@/components/Events/PrintableCheckInModal';
import ExportFieldsModal from '@/components/General/ExportFieldsModal';
import { Image } from 'expo-image';
import usePlatform from '@/utils/hooks/usePlatform';

export function PreviewModal({ isOpen, onClose, labelPreviews }) {
    return (
        <SheetModal isOpen={isOpen} onCancel={onClose} snapPoints={["80%"]} title="Preview Labels">
            <View style={{ alignItems: "center" }}>
                <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "center" }}>
                    {labelPreviews?.map((preview, i) => (
                        <Image key={i} source={{ uri: preview }} contentFit="contain" style={{ width: 300, height: 200 }} />
                    ))}
                </View>
            </View>
        </SheetModal>
    );
}

export function KioskPinModal({
    isOpen, onClose,
    kioskModeEnabled, kioskPin, setKioskPin, kioskPinForUnlock, setKioskPinForUnlock,
    storedUnlockCode, lockKioskMode, unlockKioskMode, eventInstanceID,
}) {
    const { theme: { colors } } = useUnistyles();

    const handleSubmitPin = () => {
        if (kioskModeEnabled) {
            if (kioskPinForUnlock === storedUnlockCode) {
                unlockKioskMode();
                setKioskPinForUnlock("");
                setKioskPin("");
                onClose();
            } else {
                Toast.show({ text2: "Incorrect PIN! Try again", type: "error" });
            }
        } else {
            if (kioskPin.length === 4) {
                lockKioskMode(kioskPin, eventInstanceID);
                onClose();
            } else {
                Toast.show({ text2: "PIN must be 4 digits", type: "error" });
            }
        }
    };

    return (
        <SheetModal isOpen={isOpen} onCancel={onClose} snapPoints={["80%"]} title="Kiosk Mode">
            <View style={{ alignItems: "center" }}>
                <View style={{ zIndex: 2, alignItems: "center", marginTop: 20 }}>
                    <Text style={{ color: colors.textSecondary, fontStyle: "italic", fontSize: 16, paddingBottom: 10, textAlign: "center" }}>
                        {kioskModeEnabled
                            ? "Enter your pin to disable Kiosk Mode"
                            : "Enter a PIN. You will use this PIN to unlock Kiosk Mode later. This prevents unauthorized users from access other parts of the app if the kiosk is left unattended."}
                    </Text>
                </View>

                <View style={{ width: "50%", minWidth: 300 }}>
                    <Input
                        title="PIN"
                        maxLength={4}
                        value={kioskModeEnabled ? kioskPinForUnlock : kioskPin}
                        onUpdate={(val) => kioskModeEnabled ? setKioskPinForUnlock(val) : setKioskPin(val)}
                        onEnter={handleSubmitPin}
                    />
                    <SolidButton
                        size="sm" type="success"
                        text={kioskModeEnabled ? "Exit Kiosk Mode" : "Enter Kiosk Mode"}
                        onPress={handleSubmitPin}
                    />
                </View>
            </View>
        </SheetModal>
    );
}

export function BarcodeModal({
    isOpen, onClose, onReopen,
    barcode, setBarcode,
    readyToScan, setReadyToScan,
    barcodeRef,
    peopleForNewBarcode, setPeopleForNewBarcode,
    updateBarcodesMutation,
    loggedInUser,
}) {
    const { theme: { colors, fonts } } = useUnistyles();
    const { isNative } = usePlatform();
    const pathname = usePathname();

    return (
        <SheetModal isOpen={isOpen} onCancel={onClose} snapPoints={[400]} title="Add a Barcode" stayOpenOnBlur>
            <View style={{ zIndex: 2, alignItems: "center" }}>
                <Text style={{ color: colors.textSecondary, fontStyle: "italic", fontSize: 16, paddingBottom: 10, textAlign: "center" }}>
                    Scan a barcode to add people to it
                </Text>
            </View>

            <View style={{ width: "100%", alignItems: "center", marginBottom: 20 }}>
                <Pressable
                    onPress={() => !readyToScan ? barcodeRef.current?.focus() : null}
                    style={{ backgroundColor: readyToScan ? colors.success : colors.border, paddingVertical: 3, paddingHorizontal: 7, borderRadius: 8, flexDirection: "row", alignItems: "center", gap: 5 }}
                >
                    <Unicon name={readyToScan ? "checkmark" : "arrow-forward"} size={14} colorVariant={readyToScan ? "onSuccess" : "text"} />
                    <Text style={{ color: "#ffffff", fontFamily: fonts.heading, fontSize: 15 }}>
                        {readyToScan ? "Ready to Scan" : (isNative ? "Tap here to scan" : "Click here to scan")}
                    </Text>
                </Pressable>
            </View>

            <View style={{ width: "100%" }}>
                <TextInput
                    ref={barcodeRef}
                    autoCorrect={false}
                    autoComplete="off"
                    autoCapitalize="none"
                    value={barcode}
                    style={{ color: colors.text, fontSize: 16, padding: 10, borderRadius: 8, marginBottom: 10, borderWidth: 1, borderColor: colors.border }}
                    onFocus={() => setReadyToScan(true)}
                    onBlur={() => setReadyToScan(false)}
                    onChangeText={setBarcode}
                />
            </View>

            <View>
                <SheetSelector
                    onOpen={() => onClose()}
                    onClose={() => onReopen?.()}
                    multiple
                    type="people"
                    title="People"
                    addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null}
                    fromScreen={pathname}
                    value={peopleForNewBarcode || []}
                    onChange={setPeopleForNewBarcode}
                />
            </View>

            <View style={{ width: "100%", alignItems: "center", marginTop: 20 }}>
                <SolidButton
                    disabled={peopleForNewBarcode.length === 0 || barcode.length === 0}
                    processing={updateBarcodesMutation.isPending}
                    width={200} size="sm" type="success" text="Attach Barcode"
                    onPress={() => {
                        updateBarcodesMutation.mutate({
                            personIDs: peopleForNewBarcode.map(p => p.id),
                            barcode,
                        });
                    }}
                />
            </View>
        </SheetModal>
    );
}

export function QuickAddModal({ isOpen, onClose, onCheckin }) {
    const pathname = usePathname();
    
    return (
        <SheetModal isOpen={isOpen} onClose={onClose} title="Quick Add Person" stayOpenOnBlur>
            <AddPersonQuickForm
                fromScreen={pathname}
                fromStoreKey="quickAdd"
                onSuccess={(newPerson, variables) => {
                    onClose();
                    const familyMemberIDs = variables?.Family?.FamilyMembers?.map(m => m.id).filter(Boolean) || [];
                    onCheckin?.([newPerson.id, ...familyMemberIDs]);
                    Toast.show({ type: 'success', text2: `${newPerson.firstName} ${newPerson.lastName} added and checked in!` });
                }}
            />
        </SheetModal>
    );
}

export function CheckinPrintSheetModal({ isOpen, onClose, eventInstance }) {
    return (
        <PrintableCheckInModal
            isOpen={isOpen}
            onClose={onClose}
            eventInstance={eventInstance}
        />
    );
}

export function CheckinExportModal({ isOpen, onClose, onExport, checkinFields, exportCount }) {
    return (
        <ExportFieldsModal
            isOpen={isOpen}
            onClose={onClose}
            onExport={onExport}
            title="Export People"
            checkinFields={checkinFields}
            exportCount={exportCount}
        />
    );
}

export function AnonymousHeadCountModal({ isOpen, onClose, eventInstance, attendanceCount, attendance, onUpdate }) {
    const { theme: { colors, fonts } } = useUnistyles();
    const [inputValue, setInputValue] = useState('');

    const currentAnonymousHeadCount = eventInstance?.anonymousHeadCount || 0;
    const totalCount = attendanceCount + currentAnonymousHeadCount;

    // Calculate location breakdown
    const locationBreakdown = attendance?.reduce((acc, record) => {
        const locationName = record.Location?.name || 'No Location';
        acc[locationName] = (acc[locationName] || 0) + 1;
        return acc;
    }, {}) || {};

    // Calculate checked out breakdown
    const checkedOutBreakdown = attendance?.filter(a => a.checkedOutAt).reduce((acc, record) => {
        const locationName = record.Location?.name || 'No Location';
        acc[locationName] = (acc[locationName] || 0) + 1;
        return acc;
    }, {}) || {};

    const totalCheckedOut = attendance?.filter(a => a.checkedOutAt).length || 0;

    const handleClose = () => {
        if (inputValue) {
            const newTotal = parseInt(inputValue, 10);
            if (!isNaN(newTotal) && newTotal >= attendanceCount) {
                const newAnonymousCount = newTotal - attendanceCount;
                onUpdate(newAnonymousCount);
            }
        }
        setInputValue('');
        onClose();
    };

    const handleIncrement = () => {
        const currentTotal = inputValue ? parseInt(inputValue, 10) : totalCount;
        setInputValue((currentTotal + 1).toString());
    };

    const handleDecrement = () => {
        const currentTotal = inputValue ? parseInt(inputValue, 10) : totalCount;
        if (currentTotal > attendanceCount) {
            setInputValue((currentTotal - 1).toString());
        }
    };

    return (
        <SheetModal isOpen={isOpen} onCancel={handleClose} snapPoints={["50%"]} title="Attendance Details">
            <View style={{ flex: 1, alignItems: "center", paddingVertical: 20 }}>
                {/* Location Breakdown */}
                <View style={{ width: "100%", marginBottom: 20, paddingHorizontal: 20 }}>
                    <Text style={{ color: colors.textSecondary, fontSize: 14, marginBottom: 10 }}>
                        Checked In by Location
                    </Text>
                    {Object.entries(locationBreakdown).map(([location, count]) => (
                        <View key={location} style={{ flexDirection: "row", justifyContent: "space-between", paddingVertical: 5 }}>
                            <Text style={{ color: colors.text, fontSize: 16 }}>{location}</Text>
                            <Text style={{ color: colors.text, fontSize: 16, fontFamily: fonts.heading }}>{count}</Text>
                        </View>
                    ))}
                </View>

                {/* Checked Out Breakdown */}
                <View style={{ width: "100%", marginBottom: 30, paddingHorizontal: 20 }}>
                    <Text style={{ color: colors.textSecondary, fontSize: 14, marginBottom: 10 }}>
                        Checked Out by Location ({totalCheckedOut})
                    </Text>
                    {Object.keys(checkedOutBreakdown).length > 0 ? (
                        Object.entries(checkedOutBreakdown).map(([location, count]) => (
                            <View key={location} style={{ flexDirection: "row", justifyContent: "space-between", paddingVertical: 5 }}>
                                <Text style={{ color: colors.text, fontSize: 16 }}>{location}</Text>
                                <Text style={{ color: colors.text, fontSize: 16, fontFamily: fonts.heading }}>{count}</Text>
                            </View>
                        ))
                    ) : (
                        <Text style={{ color: colors.textSecondary, fontSize: 14, fontStyle: "italic" }}>
                            No one checked out yet
                        </Text>
                    )}
                </View>

                {/* Total Headcount */}
                <View style={{ alignItems: "center", marginBottom: 20 }}>
                    <Text style={{ color: colors.textSecondary, fontSize: 14, marginBottom: 10 }}>
                        Total Headcount
                    </Text>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 20 }}>
                        <Pressable onPress={handleDecrement} style={{ padding: 10 }}>
                            <Unicon name="remove-circle" size={36} colorVariant="textSecondary" />
                        </Pressable>
                        <TextInput
                            style={{
                                color: colors.text,
                                fontSize: 72,
                                fontFamily: fonts.heading,
                                textAlign: "center",
                                minWidth: 200,
                                maxWidth: 300,
                                padding: 10,
                            }}
                            value={inputValue}
                            onChangeText={setInputValue}
                            placeholder={totalCount.toString()}
                            placeholderTextColor={colors.textSecondary}
                            keyboardType="number-pad"
                        />
                        <Pressable onPress={handleIncrement} style={{ padding: 10 }}>
                            <Unicon name="add-circle" size={36} colorVariant="textSecondary" />
                        </Pressable>
                    </View>
                </View>

                {/* Anonymous count preview */}
                <Text style={{ color: colors.textSecondary, fontSize: 14, marginBottom: 10, textAlign: "center" }}>
                    Tracked: {attendanceCount} | Anonymous: {inputValue ? Math.max(0, parseInt(inputValue, 10) - attendanceCount) : currentAnonymousHeadCount}
                </Text>

                <Text style={{ color: colors.textSecondary, fontSize: 12, textAlign: "center" }}>
                    Changes save automatically when you close
                </Text>
            </View>
        </SheetModal>
    );
}
